import { z } from 'zod';

import { UnitType } from '@optical/utils';

import { ShotContention, ShotLoad, ShotOrigin, ShotOriginArea, ShotResult } from '../statistics';
import { CalculationMode, calculationModeSchema } from './calculation-mode';
import { ExploreColumnType } from './explore-column-type';

export type ExploreColumn<TType extends ExploreColumnType = ExploreColumnType> =
  ExploreColumnByType[TType];

export type ExploreColumnDTO<TType extends ExploreColumnType = ExploreColumnType> =
  ExploreColumnDTOByType[TType];

export type ExploreColumnByType = {
  [ExploreColumnType.DISTANCE]: DistanceExploreColumn;
  [ExploreColumnType.LOAD]: LoadExploreColumn;
  [ExploreColumnType.SHOTS]: ShotsExploreColumn;
  [ExploreColumnType.SPRINTS]: SprintsExploreColumn;
};

export type ExploreColumnDTOByType = {
  [ExploreColumnType.DISTANCE]: DistanceExploreColumnDTO;
  [ExploreColumnType.LOAD]: LoadExploreColumnDTO;
  [ExploreColumnType.SHOTS]: ShotsExploreColumnDTO;
  [ExploreColumnType.SPRINTS]: SprintsExploreColumnDTO;
};

export type DistanceExploreColumn = z.output<typeof distanceExploreColumnSchema>;
export type LoadExploreColumn = z.output<typeof loadExploreColumnSchema>;
export type ShotsExploreColumn = z.output<typeof shotsExploreColumnSchema>;
export type SprintsExploreColumn = z.output<typeof sprintsExploreColumnSchema>;

export type DistanceExploreColumnDTO = z.input<typeof distanceExploreColumnSchema>;
export type LoadExploreColumnDTO = z.input<typeof loadExploreColumnSchema>;
export type ShotsExploreColumnDTO = z.input<typeof shotsExploreColumnSchema>;
export type SprintsExploreColumnDTO = z.input<typeof sprintsExploreColumnSchema>;

const _exploreColumnSchema = z.object({
  name: z.string(),
  calculationMode: calculationModeSchema,
});

const distanceExploreColumnSchema = _exploreColumnSchema.extend({
  type: z.literal(ExploreColumnType.DISTANCE),
});

const loadExploreColumnSchema = _exploreColumnSchema.extend({
  type: z.literal(ExploreColumnType.LOAD),
});

const shotsExploreColumnSchema = _exploreColumnSchema.extend({
  type: z.literal(ExploreColumnType.SHOTS),

  shotContention: z.array(z.enum(ShotContention)).optional(),
  shotLoad: z.array(z.enum(ShotLoad)).optional(),
  shotOrigin: z.array(z.enum(ShotOrigin)).optional(),
  shotOriginArea: z.array(z.enum(ShotOriginArea)).optional(),
  shotResult: z.array(z.enum(ShotResult)).optional(),
});

const sprintsExploreColumnSchema = _exploreColumnSchema.extend({
  type: z.literal(ExploreColumnType.SPRINTS),
});

export const exploreColumnSchema = z.discriminatedUnion('type', [
  distanceExploreColumnSchema,
  loadExploreColumnSchema,
  shotsExploreColumnSchema,
  sprintsExploreColumnSchema,
]);

export const ExploreColumnUtils = {
  getUnitType(column: ExploreColumn): UnitType {
    switch (column.type) {
      case ExploreColumnType.DISTANCE:
        return UnitType.LENGTH;
      case ExploreColumnType.LOAD:
        return UnitType.ABSOLUTE;
      case ExploreColumnType.SHOTS:
        return column.calculationMode === CalculationMode.PERCENTAGE
          ? UnitType.PERCENTAGE
          : UnitType.ABSOLUTE;
      case ExploreColumnType.SPRINTS:
        return UnitType.ABSOLUTE;
    }
  },
};
